/**
 * Export the CoA mapping lines that failed validation to an Excel file.
 *
 * When creating a Business Partner with a CoA mapping, the backend returns a flat
 * list of error message strings (each embedding "Row number N."). This util
 * re-reads the originally uploaded spreadsheet and produces an Excel file
 * containing only the failing rows — their original source columns plus an
 * "Error" column — so the user can fix them directly.
 */
import * as XLSX from "xlsx";

/** Source column headers expected in a CoA mapping file (matches the backend parser). */
const SOURCE_COLUMNS = [
    "Third Account Code",
    "Third Account Name",
    "Platform Account Code",
    "Platform Account Name",
    "Level",
] as const;

type TSourceColumn = (typeof SOURCE_COLUMNS)[number];

export type TCoaErrorRow = {
    Row: number;
    Error: string;
} & Record<TSourceColumn, string>;

export type TCoaErrorExport = {
    fileLevelErrors: string[];
    rowErrors: TCoaErrorRow[];
};

const norm = (value: unknown): string => String(value ?? "").trim();

/** Strip the redundant trailing "Row number N." (with its separator) from a message. */
const cleanMessage = (message: string): string =>
    message.replace(/[\s.]*Row number \d+\.?\s*$/i, "").trim();

/**
 * Locate the header row (the first row containing all required column names) and
 * return a map of column name -> column index. Falls back to row 0. Returns null
 * if the required columns can't be found.
 */
function resolveColumns(
    sheetRows: string[][],
): { headerIndex: number; columns: Record<TSourceColumn, number> } | null {
    const required = SOURCE_COLUMNS.map((c) => c.toLowerCase());
    const scanLimit = Math.min(sheetRows.length, 20);
    for (let i = 0; i < scanLimit; i++) {
        const row = sheetRows[i] ?? [];
        const lowered = row.map((cell) => norm(cell).toLowerCase());
        if (required.every((name) => lowered.includes(name))) {
            const columns = {} as Record<TSourceColumn, number>;
            for (const name of SOURCE_COLUMNS) {
                columns[name] = lowered.indexOf(name.toLowerCase());
            }
            return { headerIndex: i, columns };
        }
    }
    return null;
}

/**
 * Pure builder: given the raw sheet rows (array-of-arrays, header on some row) and
 * the backend error strings, produce the file-level errors and the per-row errors
 * with their source cell values.
 *
 * Row numbers in error strings are 1-based Excel rows (header on row 1), so the
 * data for "Row number N" lives at sheetRows[N - 1].
 */
export function buildCoaErrorRows(sheetRows: string[][], errors: string[]): TCoaErrorExport {
    const resolved = resolveColumns(sheetRows);
    const fileLevelErrors: string[] = [];
    const messagesByRow = new Map<number, string[]>();

    for (const error of errors) {
        const match = error.match(/Row number (\d+)/i);
        if (!match) {
            fileLevelErrors.push(error.trim());
            continue;
        }
        const rowNumber = Number(match[1]);
        const list = messagesByRow.get(rowNumber) ?? [];
        list.push(cleanMessage(error));
        messagesByRow.set(rowNumber, list);
    }

    const rowErrors: TCoaErrorRow[] = [];
    for (const rowNumber of [...messagesByRow.keys()].sort((a, b) => a - b)) {
        const messages = messagesByRow.get(rowNumber) ?? [];
        const sourceRow = sheetRows[rowNumber - 1];
        const entry = { Row: rowNumber, Error: messages.join("; ") } as TCoaErrorRow;
        for (const name of SOURCE_COLUMNS) {
            const index = resolved?.columns[name] ?? -1;
            entry[name] = index >= 0 && sourceRow ? norm(sourceRow[index]) : "";
        }
        rowErrors.push(entry);
    }

    return { fileLevelErrors, rowErrors };
}

/** Column order for the exported worksheet. */
const OUTPUT_HEADERS = ["Row", ...SOURCE_COLUMNS, "Error"] as const;

function buildWorksheet(data: TCoaErrorExport): XLSX.WorkSheet {
    const aoa: (string | number)[][] = [];
    if (data.fileLevelErrors.length > 0) {
        aoa.push(["File-level errors"]);
        for (const message of data.fileLevelErrors) {
            aoa.push([message]);
        }
        aoa.push([]);
    }
    aoa.push([...OUTPUT_HEADERS]);
    for (const row of data.rowErrors) {
        aoa.push(OUTPUT_HEADERS.map((header) => row[header as keyof TCoaErrorRow] ?? ""));
    }
    return XLSX.utils.aoa_to_sheet(aoa);
}

/** Fallback worksheet built straight from the error strings (Row + Error only). */
function buildFallbackWorksheet(errors: string[]): XLSX.WorkSheet {
    const aoa: (string | number)[][] = [["Row", "Error"]];
    for (const error of errors) {
        const match = error.match(/Row number (\d+)/i);
        aoa.push([match ? Number(match[1]) : "", cleanMessage(error) || error.trim()]);
    }
    return XLSX.utils.aoa_to_sheet(aoa);
}

async function readSheetRows(file: File): Promise<string[][]> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = firstSheetName ? workbook.Sheets[firstSheetName] : undefined;
    if (!worksheet) {
        throw new Error("No worksheet found");
    }
    return XLSX.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        defval: "",
        raw: false,
        blankrows: false,
    });
}

/**
 * Re-read the uploaded CoA file, build the error export, and trigger a browser
 * download of the resulting .xlsx. If the file can't be re-parsed or the required
 * columns are missing, falls back to a plain Row/Error sheet so the export never
 * silently fails.
 */
export async function exportCoaErrorsToExcel(
    file: File | null,
    errors: string[],
    filenameBase: string,
): Promise<void> {
    const workbook = XLSX.utils.book_new();
    let worksheet: XLSX.WorkSheet;
    try {
        if (!file) {
            throw new Error("No source file");
        }
        const sheetRows = await readSheetRows(file);
        const data = buildCoaErrorRows(sheetRows, errors);
        // If we couldn't resolve source columns, every source cell is blank — use the
        // simpler fallback layout instead.
        const hasSourceData = data.rowErrors.some((row) =>
            SOURCE_COLUMNS.some((column) => row[column] !== ""),
        );
        worksheet = hasSourceData ? buildWorksheet(data) : buildFallbackWorksheet(errors);
    } catch {
        worksheet = buildFallbackWorksheet(errors);
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
    const filename = filenameBase.toLowerCase().endsWith(".xlsx")
        ? filenameBase
        : `${filenameBase}.xlsx`;
    XLSX.writeFile(workbook, filename);
}
